import React from 'react';
import Icon from '../../../components/AppIcon';

const SystemHealthDashboard = ({ systemHealth, loading, onRefresh }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
        </div>
      </div>
    );
  }

  const metrics = [
    {
      label: 'Broken Auth States',
      value: systemHealth?.brokenAuthStates || 0,
      icon: 'UserX',
      color: systemHealth?.brokenAuthStates > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400',
      bgColor: systemHealth?.brokenAuthStates > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20',
      status: systemHealth?.brokenAuthStates > 0 ? 'Needs Attention' : 'Healthy'
    },
    {
      label: 'CAPTCHA Failures (24h)',
      value: systemHealth?.captchaFailures24h || 0,
      icon: 'Shield',
      color: systemHealth?.captchaFailures24h > 10 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400',
      bgColor: systemHealth?.captchaFailures24h > 10 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-green-50 dark:bg-green-900/20',
      status: systemHealth?.captchaFailures24h > 10 ? 'Elevated' : 'Normal'
    },
    {
      label: 'Config Issues',
      value: systemHealth?.configIssues || 0,
      icon: 'Database',
      color: systemHealth?.configIssues > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400',
      bgColor: systemHealth?.configIssues > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20',
      status: systemHealth?.configIssues > 0 ? 'Issues Detected' : 'Validated'
    },
    {
      label: 'Failed User Creations',
      value: systemHealth?.failedUserCreations || 0,
      icon: 'UserPlus',
      color: systemHealth?.failedUserCreations > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400',
      bgColor: systemHealth?.failedUserCreations > 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-green-50 dark:bg-green-900/20',
      status: systemHealth?.failedUserCreations > 0 ? 'Pending Retry' : 'All Clear'
    }
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Icon name="Activity" size={20} className="text-emerald-600 dark:text-emerald-400" />
          System Health
        </h2>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          Real-time monitoring indicators
        </p>
      </div>

      <div className="p-6 space-y-4">
        {metrics?.map((metric, index) => (
          <div
            key={index}
            className={`${metric?.bgColor} rounded-lg p-4 border border-gray-200 dark:border-slate-700`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${metric?.bgColor}`}>
                  <Icon name={metric?.icon} size={20} className={metric?.color} />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {metric?.label}
                  </p>
                  <p className={`text-2xl font-bold ${metric?.color} mt-1`}>
                    {metric?.value}
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs font-bold rounded ${metric?.color} ${metric?.bgColor}`}>
                {metric?.status}
              </span>
            </div>
          </div>
        ))}

        {/* Last Updated */}
        {systemHealth?.lastUpdated && (
          <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Icon name="Clock" size={12} />
              Last updated: {new Date(systemHealth?.lastUpdated)?.toLocaleString()}
            </p>
          </div>
        )}

        {/* Emergency Protocols */}
        <div className="pt-4 border-t border-gray-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            Emergency Protocols
          </h3>
          <div className="space-y-2">
            <button
              onClick={onRefresh}
              className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
            >
              <Icon name="RefreshCw" size={14} />
              Force System Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthDashboard;